import classNames from "classnames";
import { FC, useContext, useRef } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import useToggle from "../../hooks/useToggle";
import { useI18n } from "../../hooks/useTranslations";
import { useMeApi } from "../../http/me/api";
import { LanguageCode } from "../../types";
import SvgFrance from "../icons/flags/Fance";
import SvgGermany from "../icons/flags/Germany";
import SvgItaly from "../icons/flags/Italy";
import SvgSpain from "../icons/flags/Spain";
import SvgUnitedKingdom from "../icons/flags/UK";
import SvgUsa from "../icons/flags/Usa";
import styles from "./LanguageSelector.module.css";

const LanguageSelector: FC = () => {
  const i18n = useI18n();
  const meApi = useMeApi();
  const { isOpen, close, toggle } = useToggle();
  const dispatch = useContext(NotificationDispatch);
  const languageSelectorRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(languageSelectorRef, () => isOpen && close());

  const submitLanguage = async (language: LanguageCode) => {
    try {
      i18n.setLanguage(language);
      await meApi.updateMyLanguage({ preferredLanguage: language });
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      close();
    }
  };

  return (
    <>
      <div className={styles.container}>
        <button
          type="button"
          className={classNames(styles.button, styles["current-flag"], isOpen && styles.open)}
          onClick={toggle}
        >
          {i18n.language === "it-IT" ? (
            <SvgItaly />
          ) : i18n.language === "de-DE" ? (
            <SvgGermany />
          ) : i18n.language === "en-gb" ? (
            <SvgUnitedKingdom />
          ) : i18n.language === "en-US" ? (
            <SvgUsa />
          ) : i18n.language === "fr-FR" ? (
            <SvgFrance />
          ) : (
            <SvgSpain />
          )}
          <div className="ripple-container"></div>
        </button>
      </div>
      <div
        ref={languageSelectorRef}
        className={classNames(styles.languageDropdown, isOpen ? styles.visible : undefined)}
      >
        <ul>
          <li onClick={() => submitLanguage("it-IT")}>
            <SvgItaly />
            <span>{i18n.t.common.italian}</span>
          </li>
          <li onClick={() => submitLanguage("de-DE")}>
            <SvgGermany />
            <span>{i18n.t.common.german}</span>
          </li>
          <li onClick={() => submitLanguage("en-gb")}>
            <SvgUnitedKingdom />
            <span>{i18n.t.common.englishUk}</span>
          </li>
          <li onClick={() => submitLanguage("en-US")}>
            <SvgUsa />
            <span>{i18n.t.common.englishUs}</span>
          </li>
          <li onClick={() => submitLanguage("fr-FR")}>
            <SvgFrance />
            <span>{i18n.t.common.french}</span>
          </li>
          <li onClick={() => submitLanguage("es-ES")}>
            <SvgSpain />
            <span>{i18n.t.common.spanish}</span>
          </li>
        </ul>
      </div>
    </>
  );
};

export default LanguageSelector;
